import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { Switch } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { CheckCircleIcon } from '@heroicons/react/solid'

import classNames from '@/utils/classNamesLocal'
import Card from '@/shared/Card'
import { randomToastSuccess } from '@/shared/Toast'
import TipTapProject from '@/shared/TipTapProject'
import PrimaryButton from '@/shared/Buttons/Primary'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { Required } from '@/pages/ManageProjects/ProjectEdit/RequiredInformation'

const categories = {
  Animation: 'Anime, cartoons, stop-motion, 2D, 3D',
  Commercial: 'Ads, explainers, e-learning, phone menus, corporate',
  Fandub: 'Literally just fandubs',
  Film: 'Feature, short, student film, scripted or reality show, documentary',
  Music: 'Songs, background music, sound effects',
  Podcast: 'Podcasts, radio dramas, hosts',
  Roleplay: 'Roleplays, script reads',
  Videogame: 'Videogames',
  VisualNovel: 'Audiobooks, visual novels, light novels, comics',
  Other: 'Doesn\'t fit anywhere above',
  Unsure: 'A moderator picks for you. Hidden from search until they do.'
}

const catImages = {
  Animation: 'https://images.castingcall.club/icons/ICONS-animation.svg',
  Commercial: 'https://images.castingcall.club/icons/ICONS-commercial.svg',
  Fandub: 'https://images.castingcall.club/icons/ICONS-fan.svg',
  Film: 'https://images.castingcall.club/icons/ICONS-machinima.svg',
  Music: 'https://images.castingcall.club/icons/ICONS-music.svg',
  Podcast: 'https://images.castingcall.club/icons/ICONS-podcast.svg',
  Roleplay: 'https://images.castingcall.club/icons/ICONS-roleplay.svg',
  Videogame: 'https://images.castingcall.club/icons/ICONS-videogame.svg',
  VisualNovel: 'https://images.castingcall.club/icons/ICONS-visualnovel.svg',
  Other: 'https://images.castingcall.club/icons/ICONS-other.svg',
  Unsure: 'https://images.castingcall.club/icons/ICONS-unsure.svg'
}

const classifications = {
  Original: { blurb: 'Your own world, your own characters. Brave.', image: 'https://images.castingcall.club/icons/ICONS-original.svg' },
  Fan: { blurb: 'Built on something that already exists. Respect.', image: 'https://images.castingcall.club/icons/ICONS-fabdub.svg' }
}

const ChoiceCard = ({ selected, label, blurb, image, onClick, compact }) => (
  <button type='button' onClick={onClick} aria-pressed={selected}
    className={classNames('flex w-full items-start gap-x-2 rounded-lg border-2 p-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cccblue',
      selected ? 'border-cccblue bg-cccblue/10' : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500')}>
    <img className={compact ? 'h-7 w-7 flex-shrink-0' : 'h-9 w-9 flex-shrink-0'} src={image} alt='' />
    <span className='min-w-0'>
      <span className='flex items-center gap-x-1 text-sm font-semibold text-gray-900 dark:text-white'>
        {label}{ selected && <CheckCircleIcon className='h-4 w-4 text-cccblue' /> }
      </span>
      <span className='block text-xs leading-snug text-gray-500 dark:text-gray-300'>{blurb}</span>
    </span>
  </button>
)

const Toggle = ({ checked, onChange, label, hint }) => (
  <Switch.Group as='div' className='flex items-start gap-x-3'>
    <Switch checked={!!checked} onChange={onChange}
      className={classNames(checked ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500', 'relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cccpurple focus:ring-offset-2')}>
      <span aria-hidden='true' className={classNames(checked ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition')} />
    </Switch>
    <Switch.Label as='span' className='flex flex-col'>
      <span className='text-sm text-gray-700 dark:text-gray-300'>{label}</span>
      { hint && <span className='text-xs text-gray-500 dark:text-gray-400'>{hint}</span> }
    </Switch.Label>
  </Switch.Group>
)

const OptionalInfoForm = forwardRef(({ initialProject, onSaved, embedded }, ref) => {
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const [project, setProject] = useState(initialProject)
  const [formattedVideoUrl, setFormattedVideoUrl] = useState(initialProject.youtubeUrl)
  const tipTapIdx = useRef(1)
  const { putpostRequest } = useQuery()
  const form = useForm({
    defaultValues: {
      youtube_url: initialProject.youtubeUrl,
      nsfw: initialProject.nsfw,
      location: initialProject.location,
      location_required: initialProject.locationRequired,
      description: initialProject.description,
      classification: initialProject.classification,
      category: initialProject.category
    }
  })
  const { formState, reset, register, unregister, watch, handleSubmit, setValue, getValues, setError, trigger } = form
  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)
  const pendingSave = useRef(null)
  const description = getValues().description

  useEffect(() => {
    register('description')
    register('category', { required: true })
    register('classification', { required: true })
    return () => { unregister('description'); unregister('category'); unregister('classification') }
  }, [register])

  const set = (key, val) => setValue(key, val, { shouldDirty: true, shouldValidate: true })
  const toggle = (key) => set(key, !getValues()[key])
  const keyPress = (e) => { if ((e.metaKey || e.ctrlKey) && e.keyCode === 13) { handleSubmit(onSubmit)() } }
  const finish = (ok) => { if (pendingSave.current) { pendingSave.current(ok); pendingSave.current = null } }

  const onSubmit = (data) => {
    setLoading(true)
    putpostRequest(`/api/v3/manage/projects/${project.id}`, 'PATCH', { project: data }, (err, jsonData) => {
      setLoading(false)
      if (err) {
        if (typeof err !== 'string') { Object.entries(err).forEach(([key, value]) => { setError(key, value) }) }
        finish(false)
        return
      }
      reset({ ...getValues(), youtube_url: jsonData.project.youtubeUrl })
      setFormattedVideoUrl(jsonData.project.youtubeUrl)
      setProject({ ...jsonData.project })
      if (onSaved) { onSaved(jsonData.project) }
      if (!embedded) { setToast(randomToastSuccess()) }
      finish(true)
    })
  }

  useImperativeHandle(ref, () => ({
    save: () => new Promise((resolve) => {
      if (!isDirty) { trigger().then(resolve); return }
      pendingSave.current = resolve
      handleSubmit(onSubmit, () => finish(false))()
    })
  }), [isDirty, handleSubmit])

  const category = watch('category')
  const classification = watch('classification')
  const locationRequired = watch('location_required')
  const nsfw = watch('nsfw')

  const footer = embedded ? null : <div className='flex justify-end'>
    <PrimaryButton disabled={!isDirty} onClick={handleSubmit(onSubmit)} loading={loading} isDirty={isDirty} text='Save' />
  </div>

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>The details</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Category and type are how talent filter the feed. Pick wrong and the right people never see you.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>The description is your pitch. Typos and walls of text scare off good talent. Steal from these:</p>
      <div className='mt-1 flex flex-col space-y-1 text-sm'>
        <a href='https://buf.notion.site/Indie-Studio-a1f2bd927bc446799ee1cf67366a0f67' target='_blank' rel='noreferrer' className='hover:underline'>Indie studio example</a>
        <a href='https://buf.notion.site/Fan-Dub-Sample-d3a5d6d6ae6d41b9881b277840bcb7ed' target='_blank' rel='noreferrer' className='hover:underline'>Fan dub example</a>
        <a href='https://buf.notion.site/Small-Original-Sample-dc1a3999617148bab774e082a1b7bb08' target='_blank' rel='noreferrer' className='hover:underline'>Small original example</a>
      </div>
    </div>
    <Card title='Details' footer={footer}>
      <form className='space-y-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Category <Required /></label>
          <div className='mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3'>
            { Object.keys(categories).map((cat) => (
              <ChoiceCard key={cat} compact label={cat === 'VisualNovel' ? 'Visual Novel' : cat} blurb={categories[cat]} image={catImages[cat]} selected={category === cat} onClick={() => set('category', cat)} />
            ))}
          </div>
          { errors.category && <div className='mt-1 text-sm text-red-600'>Pick a category so people can find you.</div> }
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Original or fan work? <Required /></label>
          <div className='mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2'>
            { Object.keys(classifications).map((cl) => (
              <ChoiceCard key={cl} label={cl} blurb={classifications[cl].blurb} image={classifications[cl].image} selected={classification === cl} onClick={() => set('classification', cl)} />
            ))}
          </div>
          { errors.classification && <div className='mt-1 text-sm text-red-600'>One or the other. I won't judge.</div> }
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Description</label>
          <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>
            Paste from Word or Google Docs and I'll tidy the formatting.
            { currentUser.canUploadImages
              ? ' Drag or paste images straight in.'
              : <> Images in the description are a <a href='/subscriptions' target='_blank' rel='noreferrer' className='underline'>Premium</a> perk.</> }
          </p>
          <TipTapProject html={description} idx={tipTapIdx.current} imageUploadSupported={currentUser.canUploadImages}
            updateHtml={(val) => set('description', val)}
            placeholder='What is it, who is it for, and why should someone spend an evening auditioning for it?' />
        </div>

        <div>
          <label htmlFor='youtube_url' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Pitch video</label>
          <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>Any YouTube or Loom link. I'll sort out the embed part.</p>
          <input id='youtube_url' onKeyDown={keyPress} type='text' className={errors.youtube_url ? 'errors' : ''} {...register('youtube_url')} placeholder='https://www.youtube.com/watch?v=...' />
          { errors.youtube_url && <div className='mt-1 text-sm text-red-600'>{errors.youtube_url[0] || errors.youtube_url.message}</div> }
          { formattedVideoUrl && (formattedVideoUrl.includes('loom.com') || formattedVideoUrl.includes('youtube.com')) && <div className='mt-2 aspect-w-16 aspect-h-9 w-full max-w-xl overflow-hidden rounded-lg'>
            <iframe src={formattedVideoUrl.includes('loom.com') ? `${formattedVideoUrl}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true` : formattedVideoUrl}
              frameBorder='0' allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' allowFullScreen title='Pitch video' />
          </div> }
        </div>

        <div className='space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700'>
          <Toggle checked={nsfw} onChange={() => toggle('nsfw')} label='This project is NSFW'
            hint={<>Nudity, gore, heavy topics. Anything past the <a href='/tos' target='_blank' rel='noreferrer' className='underline'>terms of service</a> gets pulled.</>} />
          <Toggle checked={locationRequired} onChange={() => toggle('location_required')} label='Talent has to show up in person' />
          { locationRequired && <div className='pl-14'>
            <label htmlFor='location' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Where? <Required /></label>
            <input id='location' onKeyDown={keyPress} type='text' className={errors.location ? 'errors mt-1' : 'mt-1'} {...register('location', { required: true })} placeholder='Brooklyn, NY' />
            { errors.location && <div className='mt-1 text-sm text-red-600'>In-person needs a place. Where are they going?</div> }
          </div> }
        </div>
      </form>
      { errors.base && <div className='mt-2 text-sm text-red-600'>{errors.base[0]}</div> }
    </Card>
  </div>
})

OptionalInfoForm.displayName = 'OptionalInfoForm'

export default OptionalInfoForm

OptionalInfoForm.propTypes = {
  initialProject: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
  embedded: PropTypes.bool
}
